// src/data/did-you-know-tips.ts
/**
 * "Did You Know" tips for the 8020agent app
 * These tips highlight app features that users might not discover on their own
 * Focus: What users can DO in this app, not general Google Ads advice
 */

export interface DidYouKnowTip {
  id: string;
  title: string;
  description: string;
  icon?: string; // Emoji or icon identifier
  category: 'feature' | 'optimization' | 'workflow' | 'insight' | 'advanced';
  link?: string; // Optional link to relevant page
  linkText?: string; // Text for the link button
  pages?: string[]; // Pages where this tip is most relevant (e.g., ['/analysis', '/trends'])
}

export const didYouKnowTips: DidYouKnowTip[] = [
  // Budget Optimization / Analysis Page Tips
  {
    id: 'budget-sliders',
    title: 'Drag the Sliders to Test Budgets',
    description: 'Move the budget sliders to instantly see how changing each campaign\'s budget is predicted to affect your total profit.',
    icon: '🎚️',
    category: 'feature',
    pages: ['/analysis'],
    link: '/analysis',
    linkText: 'Try It Now'
  },
  {
    id: 'analysis-scenarios',
    title: 'Scenario Tabs for Quick Analysis',
    description: 'Click the Scenario tabs (conservative, balanced, aggressive) to see how changing multiple campaign budgets are predicted to affect your total profit.',
    icon: '🎯',
    category: 'feature',
    pages: ['/analysis'],
  },
  {
    id: 'analysis-profit-totals',
    title: 'Watch the Total Profit Number',
    description: 'As you adjust budgets, the total profit number at the top updates in real-time to show your optimization impact.',
    icon: '💰',
    category: 'feature',
    pages: ['/analysis'],
  },

  // Trends Page Tips
  {
    id: 'trends-moving-average',
    title: 'Toggle Moving Average',
    description: 'Click the "Moving Average" toggle to smooth out daily fluctuations and see clearer trends in your performance data.',
    icon: '📈',
    category: 'feature',
    pages: ['/trends'],
  },
  {
    id: 'trends-pmax-channels',
    title: 'Click on PMax Campaigns',
    description: 'Click any Performance Max campaign to see the channel breakdown showing where Google is actually spending your budget.',
    icon: '🎪',
    category: 'feature',
    pages: ['/trends'],
  },
  {
    id: 'trends-charts',
    title: 'Hover Over the Charts',
    description: 'Hover your mouse over any point on the trend charts to see the exact numbers for that date.',
    icon: '📊',
    category: 'feature',
    pages: ['/trends'],
  },
  {
    id: 'trends-campaign-navigation',
    title: 'Navigate Between Campaigns',
    description: 'Use the previous/next arrow buttons to quickly move between campaigns without returning to the campaign list.',
    icon: '⬅️➡️',
    category: 'feature',
    pages: ['/trends'],
  },

  // Keywords Page Tips
  {
    id: 'keywords-table-view',
    title: 'View All Keywords in Table',
    description: 'This page shows your keywords in a sortable table view - click any column header to sort by that metric.',
    icon: '📋',
    category: 'feature',
    pages: ['/keywords'],
  },
  {
    id: 'keywords-sort-columns',
    title: 'Click Column Headers to Sort',
    description: 'Click any column header to sort your keywords by that metric - find your highest spend or worst performers instantly.',
    icon: '↕️',
    category: 'feature',
    pages: ['/keywords'],
  },
  {
    id: 'keywords-ngram-tab',
    title: 'Switch to N-gram Tab',
    description: 'Click the N-gram tab to see common word patterns in your search terms - reveals what messaging resonates.',
    icon: '📊',
    category: 'feature',
    pages: ['/keywords'],
  },
  {
    id: 'keywords-quality-tree',
    title: 'Check Quality Score Tree',
    description: 'View the color-coded quality score tree to quickly identify keywords with low quality scores that need improvement.',
    icon: '🌳',
    category: 'feature',
    pages: ['/keywords'],
  },

  // Products Page Tips
  {
    id: 'products-aggregation',
    title: 'Aggregate Products by Title or ID',
    description: 'Use the aggregation dropdown to group products by title, ID, or title and ID to see performance at different levels.',
    icon: '🗂️',
    category: 'feature',
    pages: ['/products'],
  },
  {
    id: 'products-campaign-navigation',
    title: 'Navigate Between Campaigns',
    description: 'Use the previous/next arrow buttons to quickly move between campaigns without returning to the campaign list.',
    icon: '⬅️➡️',
    category: 'feature',
    pages: ['/products'],
  },
  {
    id: 'products-three-tabs',
    title: 'Switch Between Three Product Tabs',
    description: 'Use the three tabs to view different product analysis views and find insights across different dimensions.',
    icon: '📑',
    category: 'feature',
    pages: ['/products'],
  },
  {
    id: 'products-ngrams',
    title: 'Check the Product Title N-grams',
    description: 'Scroll down to the n-gram section to see which words in your product titles correlate with better performance.',
    icon: '📝',
    category: 'feature',
    pages: ['/products'],
  },

  // Asset Performance Page Tips
  {
    id: 'asset-three-approaches',
    title: 'Three Ways to View Assets',
    description: 'Choose from three different approaches to analyze your asset performance - by type, by level, or by campaign.',
    icon: '🎯',
    category: 'feature',
    pages: ['/asset'],
  },
  {
    id: 'asset-low-impressions',
    title: 'Check Low Impression Report',
    description: 'View the low impression report to find assets that Google isn\'t showing - these may need to be removed or improved.',
    icon: '👁️',
    category: 'insight',
    pages: ['/asset'],
  },
  {
    id: 'asset-preview',
    title: 'Click Images to Preview',
    description: 'Click on any image asset to see a larger preview and view its performance metrics.',
    icon: '🖼️',
    category: 'feature',
    pages: ['/asset'],
  },
  {
    id: 'asset-sort',
    title: 'Sort by Conversion Rate',
    description: 'Click the conversion rate column to sort assets by actual conversion performance, not just impressions.',
    icon: '📈',
    category: 'feature',
    pages: ['/asset'],
  },

  // Placements Page Tips
  {
    id: 'placements-filter',
    title: 'Filter Placement Types',
    description: 'Use the filter dropdown to show only YouTube, websites, or mobile apps - makes it easier to audit each type.',
    icon: '🎛️',
    category: 'feature',
    pages: ['/placements'],
  },
  {
    id: 'placements-export-exclusions',
    title: 'Export for Exclusion Lists',
    description: 'Select poor-performing placements and export them to create exclusion lists in Google Ads.',
    icon: '🚫',
    category: 'workflow',
    pages: ['/placements'],
  },
  {
    id: 'placements-filter-exclude',
    title: 'Filter to Exclude Placements',
    description: 'Use the filter controls to identify placements you want to exclude based on performance metrics.',
    icon: '🔎',
    category: 'feature',
    pages: ['/placements'],
  },

  // Hourly Performance Page Tips
  {
    id: 'hourly-heatmap',
    title: 'Read the Heatmap Colors',
    description: 'Darker colors show higher values for the selected metric - quickly spot patterns across hours and days.',
    icon: '🎨',
    category: 'feature',
    pages: ['/hourly'],
  },
  {
    id: 'hourly-color-picker',
    title: 'Change Heatmap Colors',
    description: 'Click the color picker to customize the heatmap color scheme to match your preferences.',
    icon: '🌈',
    category: 'feature',
    pages: ['/hourly'],
  },
  {
    id: 'hourly-analysis-mode',
    title: 'Toggle Analysis Mode',
    description: 'Switch between different analysis modes to view your hourly data in different ways for deeper insights.',
    icon: '🔄',
    category: 'feature',
    pages: ['/hourly'],
  },
  {
    id: 'hourly-hover-details',
    title: 'Hover for Exact Metrics',
    description: 'Hover over any cell in the heatmap to see the exact performance numbers for that hour and day.',
    icon: '🔍',
    category: 'feature',
    pages: ['/hourly'],
  },

  // Profit Curves Page Tips
  {
    id: 'curve-response-model',
    title: 'View Response Model Curve',
    description: 'This page shows the response model curve for a single campaign - see how spend affects profit at different levels.',
    icon: '📈',
    category: 'feature',
    pages: ['/curve'],
  },
  {
    id: 'curve-sliders',
    title: 'Drag the Budget Slider',
    description: 'Use the slider to test different budget levels and see the predicted impact on profit based on the response model.',
    icon: '🎚️',
    category: 'feature',
    pages: ['/curve'],
  },
  {
    id: 'curve-optimal-point',
    title: 'Look for the Peak of the Curve',
    description: 'The highest point on the curve shows the optimal spend level for maximum profit from this campaign.',
    icon: '⛰️',
    category: 'insight',
    pages: ['/curve'],
  },

  // Settings Page Tips
  {
    id: 'settings-business-mode',
    title: 'Set Your Business Mode',
    description: 'In each account card, toggle between e-commerce and lead gen to get profit calculations that match your model.',
    icon: '🏢',
    category: 'feature',
    pages: ['/settings'],
  },
  {
    id: 'settings-refresh',
    title: 'Refresh Button Pulls Latest Data',
    description: 'Click the refresh icon on any account card to pull the most recent data from your Google Sheets.',
    icon: '🔄',
    category: 'feature',
    pages: ['/settings'],
  },
  {
    id: 'settings-cogs',
    title: 'Update Your COGS Percentage',
    description: 'Edit the COGS field in your account settings to get accurate profit calculations based on your actual margins.',
    icon: '🧮',
    category: 'feature',
    pages: ['/settings'],
  },
  {
    id: 'settings-breakeven-cpa',
    title: 'Set Your Break-even CPA',
    description: 'Enter your break-even cost per acquisition to see which campaigns are profitable and which need optimization.',
    icon: '⚖️',
    category: 'feature',
    pages: ['/settings'],
  },
  {
    id: 'settings-api-keys',
    title: 'Add Your AI API Keys',
    description: 'Scroll to API Keys section and add your OpenAI, Claude, or Gemini keys to unlock AI analysis features.',
    icon: '🔑',
    category: 'feature',
    pages: ['/settings'],
  },

  // General Tips (shown on any page)
  {
    id: 'dark-mode-toggle',
    title: 'Toggle Dark Mode',
    description: 'Click the sun/moon icon in the navigation menu to switch between light and dark mode.',
    icon: '🌙',
    category: 'feature',
  },
  {
    id: 'video-tutorials',
    title: 'Watch the Video Tutorials',
    description: 'Look for video player icons on each page - they contain short tutorials showing how to use that feature.',
    icon: '🎥',
    category: 'feature',
  },
  {
    id: 'sample-data-mode',
    title: 'Try Sample Data Mode',
    description: 'Click "Load Sample Data" in settings to explore all features with realistic demo data before connecting your account.',
    icon: '🎮',
    category: 'feature',
  },
  {
    id: 'refresh-sheet-data',
    title: 'Re-run Your Google Ads Script',
    description: 'To get fresh data, re-run your Google Ads script in Google Ads, then click refresh in Settings.',
    icon: '🔄',
    category: 'workflow',
  },
];

/**
 * Get tips relevant to a specific page
 * @param pathname Current page path (e.g., '/analysis')
 * @param shownTipIds Tips already shown (to avoid repeats)
 * @returns Array of relevant tips for this page
 */
export function getTipsForPage(pathname: string, shownTipIds: string[] = []): DidYouKnowTip[] {
  return didYouKnowTips.filter(tip =>
    !shownTipIds.includes(tip.id) &&
    tip.pages?.includes(pathname)
  );
}

/**
 * Get a random tip for a specific page
 * @param pathname Current page path
 * @param shownTipIds Tips already shown
 * @returns A random tip for this page, or null if none available
 */
export function getRandomTipForPage(pathname: string, shownTipIds: string[] = []): DidYouKnowTip | null {
  const pageTips = getTipsForPage(pathname, shownTipIds);

  if (pageTips.length === 0) {
    return null;
  }

  const randomIndex = Math.floor(Math.random() * pageTips.length);
  return pageTips[randomIndex];
}

/**
 * Get a random tip that hasn't been shown before
 * @param shownTipIds Array of tip IDs that have already been shown
 * @returns A random unshown tip, or null if all tips have been shown
 */
export function getRandomUnshownTip(shownTipIds: string[]): DidYouKnowTip | null {
  const unshownTips = didYouKnowTips.filter(tip => !shownTipIds.includes(tip.id));

  if (unshownTips.length === 0) {
    // All tips have been shown - could reset or return null
    return null;
  }

  const randomIndex = Math.floor(Math.random() * unshownTips.length);
  return unshownTips[randomIndex];
}

/**
 * Get the next tip to show based on shown history
 * Prioritizes page-relevant tips, falls back to general tips
 */
export function getNextSessionTip(
  sessionTipShown: boolean,
  allTimeShownIds: string[],
  currentPath?: string
): DidYouKnowTip | null {
  if (sessionTipShown) {
    return null; // Already shown one this session
  }

  // Try to get a page-specific tip first
  if (currentPath) {
    const pageTip = getRandomTipForPage(currentPath, allTimeShownIds);
    if (pageTip) {
      return pageTip;
    }
  }

  // Fall back to any unshown tip
  return getRandomUnshownTip(allTimeShownIds);
}
